(() => {
  if (customElements.get('orion-construction-labels')) return;

  const plainText = element => {
    const copy = element.cloneNode(true);
    copy.querySelectorAll('br').forEach(breakElement => breakElement.replaceWith(' '));
    return copy.textContent.trim();
  };
  const cleanText = text => text.replace(/\s+/g, ' ').trim();
  const imageKey = source => {
    try { return decodeURIComponent(new URL(source, location.href).pathname.split('/').pop()).toLowerCase(); }
    catch (_) { return ''; }
  };

  // Classification belongs to the reviewed source image, not the catalogue or
  // the mere presence of a construction-image reference. Unknown sources keep
  // their description and never receive a second, speculative set of labels.
  const embeddedSources = new Set([
    'sn09_wb.png',
    'orion_sn09_3d_dark_20260916_v01.png',
    'orion_sn25_3d_dark_20260916_v01.png',
    'sn09_wb_7e232536-c706-44ee-98a3-259dd926505e.png',
    'sn25_wb_1_5a26ebb0-89f1-4840-a6e3-b4b3786fecbd.png'
  ]);
  // Verified against the current PDF diagrams; the CDN only trims transparent margins.
  const recoveredSources = new Set(["485.png", "486.png", "487.png", "488.png", "489.png", "490.png", "491.png", "492.png", "493.png", "3117.png", "3119.png", "3122.png"]);
  function labelMode(image) {
    const key = imageKey(image?.dataset.fullSrc || image?.getAttribute('src') || image?.dataset.src || '');
    if (embeddedSources.has(key)) return 'embedded';
    if (recoveredSources.has(key)) return 'separate';
    if (/^(?:smart|comfort|filoxenia)_\d+_\d+_cutaway_20260914_v01\.png$/.test(key)) return 'separate';
    if (/^(?:360[0-6]-smart|560[0-6]-comfort|760[0-6]-filoxenia)(?:_[a-f0-9-]+)?\.png$/.test(key)) return 'separate';
    // ATLAS (Roll Pack): its 3D construction carries no labels (reviewed 2026-09-30).
    if (/^orion_atlas_3d_construction_\d{8}\.png$/.test(key)) return 'separate';
    return 'unknown';
  }

  // Show one compact technical label. Keep the complete, unmodified source as
  // its accessible label and tooltip, including bilingual wording and units.
  function compactLabel(text) {
    const divider = /\s+\/\s*|\/(?=(?:Fabric|Wadding|Foam|Bonnell|Pocket|Border|White|Non-woven)\b)/i.exec(text);
    if (!divider || !/[Α-ω]/.test(text.slice(0, divider.index))) return text;
    const prefix = text.slice(0, divider.index);
    const technical = text.slice(divider.index + divider[0].length).trim();
    if (!technical) return text;
    // These qualifiers carry meaning beyond the translated material name.
    if (/ΜΟΝΟ\s+ΕΠΑΝΩ/i.test(prefix)) return 'ΜΟΝΟ ΕΠΑΝΩ · ' + technical;
    if (/ΜΟΝΟ\s+ΚΑΤΩ/i.test(prefix)) return 'ΜΟΝΟ ΚΑΤΩ · ' + technical;
    return technical;
  }


  // The source lists every part from the top surface to the base. It is counted as the construction totals count
  // it (snippets/orion-construction-data.liquid): the side border and the perimeter support wrap the core and are
  // not layers, and a double pocket unit is two. A part the source broke over two lines joins back into one.
  const borderPart = text => /Φάσα|\bBorder\b|\bFascia\b/i.test(text);
  const sidePart = text => borderPart(text) || /Περιμετρ|perimeter|Foam board|Foam support/i.test(text);
  const doubleUnit = text => /Δύο\s+σειρές|2\s+σειρές|Two rows|2 rows|Double[\s-]+layered|Double pocket/i.test(text);
  function joins(previous, text) {
    if (/(?:[\/,&]|\s(?:και|με|with|and))$/iu.test(previous) || /^\p{Ll}/u.test(text)) return ' ';
    if ((borderPart(previous) && borderPart(text)) || (doubleUnit(previous) && doubleUnit(text))) return ' / ';
    return null;
  }
  function constructionModel(items) {
    const parts = [];
    items.forEach(source => {
      const text = cleanText(plainText(source)).replace(/\bHight\b/g, 'High');
      const last = parts[parts.length - 1];
      const glue = last ? joins(last.text, text) : null;
      if (glue === null) parts.push({ text });
      else last.text = cleanText(last.text + glue + text);
    });
    const pad = value => String(value).padStart(2, '0');
    let layers = 0;
    let span = false;
    parts.forEach(part => {
      part.side = sidePart(part.text);
      if (part.side) return;
      const width = doubleUnit(part.text) ? 2 : 1;
      part.n = width === 2 ? pad(layers + 1) + '–' + pad(layers + 2) : pad(layers + 1);
      span ||= width === 2;
      layers += width;
    });
    const side = parts.filter(part => part.side);
    if (side[0]) side[0].first = true;
    return { parts: [...parts.filter(part => !part.side), ...side], layers, span };
  }
  // The materials beside every 3D are written in English, on every page, as the ARMONIA pictures print theirs (the
  // owner's rule, 2026-10-01): on a page in another language they come from the product's English description
  // (templates/product.layers.liquid at the page's English address); the page's own list is still the one hidden.
  const sideNote = () => 'At the sides · not layers';
  async function englishItems() {
    if (/^en\b/i.test(document.documentElement.lang)) return null;
    const alternate = document.querySelector('link[rel="alternate"][hreflang="en"]');
    const path = alternate ? new URL(alternate.href, location.href).pathname : '/en' + location.pathname;
    try {
      const response = await fetch(path + '?view=layers', { credentials: 'same-origin' });
      if (!response.ok) return null;
      const english = new DOMParser().parseFromString(await response.text(), 'text/html');
      const lists = sourceLists(english.body);
      return lists.length === 1 ? lists[0].items : null;
    } catch (_) {
      return null;
    }
  }

  function sourceLists(description) {
    const materialHeadings = [...description.querySelectorAll('h2,h3,h4')]
      // ATLAS (Roll Pack) lists its layers under "Η κατασκευή, από πάνω προς τα κάτω" / "Construction, from top to bottom".
      .filter(heading => /^(?:υλικά|materials|construction materials|η κατασκευή, από πάνω προς τα κάτω|construction, from top to bottom)$/i.test(cleanText(heading.textContent)));
    return materialHeadings.map(heading => {
      let list = heading.nextElementSibling;
      while (list && !list.textContent.trim() && !/^H[1-6]$/.test(list.tagName)) list = list.nextElementSibling;
      if (!list || !['UL', 'OL'].includes(list.tagName)) return null;
      const items = [...list.children].filter(item => item.tagName === 'LI');
      if (!items.length || items.some(item => !item.textContent.trim())) return null;
      return { heading, list, items };
    }).filter(Boolean);
  }

  class OrionConstructionLabels extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._abort = new AbortController();
      const start = () => { this._frame = requestAnimationFrame(() => this.enhance()); };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true, signal: this._abort.signal });
      else start();
    }

    // Layers carry their number; the side parts follow them under a caption of their own.
    makeList(model) {
      const list = document.createElement('ol');
      list.className = 'orion-construction-labels__list';
      list.classList.toggle('has-span', model.span);
      list.setAttribute('aria-label', this.dataset.listLabel);
      // Explicit role preserves list semantics when markers are removed.
      list.setAttribute('role', 'list');
      model.parts.forEach(part => {
        const item = document.createElement('li');
        item.textContent = compactLabel(part.text);
        item.title = part.text;
        if (part.side) {
          item.className = 'is-side';
          if (part.first) item.dataset.note = sideNote();
          item.setAttribute('aria-label', sideNote() + ': ' + part.text);
        } else {
          item.dataset.n = part.n;
          item.setAttribute('aria-label', part.text);
        }
        list.appendChild(item);
      });
      return list;
    }

    async enhance() {
      if (this._enhanced || this._enhancing || !this.isConnected) return;
      const root = this.closest('[data-orion-mattress-product], product-info');
      const gallery = root?.querySelector('orion-mattress-gallery');
      const standardTemplate = root?.matches('product-info');
      const description = standardTemplate
        ? root.closest('main')?.querySelector('#product-description-container')
        : root?.querySelector('.orion-mattress-product__description, .orion-story__lead');
      if (!gallery || !description) return;
      const sources = sourceLists(description);
      if (sources.length !== 1) return; // Never guess which unrelated list to move.
      const source = sources[0];
      const anatomy = root.querySelector('.orion-mattress-product__anatomy');
      const anatomyButton = anatomy?.querySelector('[data-orion-layer-zoom]');
      const anatomyImage = anatomyButton?.querySelector('img');
      const lowerKey = imageKey(anatomyImage?.getAttribute('src') || '');
      const views = [...gallery.querySelectorAll('[data-view]')];
      const cutaways = views.filter(view => view.dataset.kind === 'cutaway' || (lowerKey && imageKey(view.querySelector('img')?.dataset.fullSrc || '') === lowerKey));
      if (!cutaways.length) return;
      if (anatomy && (!anatomyButton || !anatomyImage)) return;

      this._gallery = gallery;
      this._source = source;
      // A tall picture (ATLAS's front view of the stack) keeps its words beside it everywhere, phones included, as the
      // ARMONIA pictures do; the square 3D pictures keep theirs beside or below (assets/orion-construction-labels.css).
      const shape = cutaways[0].querySelector('img');
      const ratio = Number(shape?.getAttribute('width')) / Number(shape?.getAttribute('height'));
      const tall = ratio > 0 && ratio < .75;
      const markTall = layout => {
        if (!tall) return;
        layout.classList.add('orion-construction-layout--tall');
        layout.style.setProperty('--orion-stack-ratio', ratio.toFixed(4));
      };
      this._decorated = [];
      this._sourceState = [];
      this._legacy = [];
      const modes = cutaways.map(view => labelMode(view.querySelector('img')));
      const lowerMode = anatomyImage ? labelMode(anatomyImage) : null;
      if (modes.every(mode => mode === 'embedded') && (!anatomyImage || lowerMode === 'embedded')) {
        // The complete original diagram already contains its labels. Retain its
        // full width in both displays and in the existing full-screen viewer.
        if (anatomyButton) this.attachViewer(anatomyButton, gallery, cutaways[0]);
        this._sourceState = [source.heading, source.list].map(element => ({ element, hidden: element.hidden }));
        this._sourceState.forEach(({ element }) => { element.hidden = true; element.classList.add('orion-construction-source'); });
        this._enhanced = true;
        this.dataset.ready = 'embedded';
        return;
      }
      if (modes.some(mode => mode !== 'separate') || lowerMode === 'embedded') {
        this.dataset.ready = 'source-review-required';
        return;
      }
      this._enhancing = true;
      const english = await englishItems();
      this._enhancing = false;
      if (!this.isConnected || this._enhanced) return;
      const model = constructionModel(english || source.items);
      // The count above the stack comes from the audited totals; a list that disagrees with it is marked for review.
      const audited = Number(anatomy?.querySelector('[data-orion-anatomy-count]')?.dataset.orionAnatomyCount);
      if (audited && audited !== model.layers) this.dataset.layerMismatch = audited + '/' + model.layers;
      const reviewedImage = cutaways[0].querySelector('img');
      const reviewedKey = imageKey(reviewedImage.dataset.fullSrc || reviewedImage.getAttribute('src') || '');
      const recoveredCutaway = recoveredSources.has(reviewedKey);
      if (anatomyImage && (lowerKey !== reviewedKey || recoveredCutaway)) {
        // Use the exact reviewed, unlabelled cutaway shown above. A separate
        // legacy lower image must not silently receive labels of its own.
        const attributes = ['src', 'srcset', 'sizes', 'width', 'height'];
        this._anatomyImageSource = { image: anatomyImage, attributes: attributes.map(name => [name, anatomyImage.getAttribute(name)]) };
        anatomyImage.src = reviewedImage.dataset.src || reviewedImage.getAttribute('src');
        anatomyImage.srcset = reviewedImage.dataset.srcset || reviewedImage.getAttribute('srcset') || '';
        anatomyImage.sizes = '(min-width: 990px) 400px, 45vw';
        for (const name of ['width', 'height']) anatomyImage.setAttribute(name, reviewedImage.getAttribute(name));
      }
      for (const view of cutaways) {
        const plane = view.querySelector('[data-image-plane]');
        if (!plane || !plane.querySelector('img')) { this.restore(); return; }
        const list = this.makeList(model);
        plane.classList.add('orion-construction-layout');
        plane.style.setProperty('--orion-layer-count', model.parts.length);
        markTall(plane);
        plane.appendChild(list);
        view.classList.add('orion-construction-view');
        this._decorated.push({ view, plane, list });
      }

      if (anatomyButton) {
        const layout = document.createElement('div');
        layout.className = 'orion-construction-layout orion-construction-layout--anatomy';
        layout.style.setProperty('--orion-layer-count', model.parts.length);
        markTall(layout);
        anatomyButton.before(layout);
        layout.append(anatomyButton, this.makeList(model));
        this._anatomy = { layout, button: anatomyButton };
        // On the refreshed page a phone shows the stack first and opens its materials from a button under it
        // (assets/orion-construction-labels.css); wider screens always show them beside the stack.
        if (anatomy.classList.contains('orion-anatomy') && !tall) {
          const list = layout.querySelector('.orion-construction-labels__list');
          const toggle = document.createElement('button');
          toggle.type = 'button';
          toggle.className = 'orion-construction-labels__toggle';
          toggle.setAttribute('aria-expanded', 'false');
          list.id = 'OrionConstructionList-' + (this.dataset.sectionId || 'page');
          toggle.setAttribute('aria-controls', list.id);
          toggle.innerHTML = '<span></span><small></small><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
          toggle.querySelector('span').textContent = this.dataset.listLabel;
          toggle.querySelector('small').textContent = String(model.layers);
          toggle.addEventListener('click', () => {
            const open = toggle.getAttribute('aria-expanded') !== 'true';
            toggle.setAttribute('aria-expanded', String(open));
            layout.classList.toggle('is-open', open);
          }, { signal: this._abort.signal });
          list.before(toggle);
        }
        const captionNodes = [...anatomyButton.childNodes].filter(node => node.nodeType === Node.TEXT_NODE);
        if (captionNodes.some(node => node.textContent.trim())) {
          const caption = document.createElement('p');
          caption.className = 'orion-construction-labels__caption';
          captionNodes.forEach(node => caption.appendChild(node));
          layout.appendChild(caption);
          this._anatomy.caption = caption;
        }
        // The labelled 3D below zooms out of itself with the gallery's viewer; the gallery above does not move.
        this.attachOriginViewer(anatomyButton, gallery, cutaways[0]);
      }

      // A small set of mattresses uses Dawn's original description tabs.
      // Pair the cutaway with its labels there as well; never leave an empty
      // Materials tab or alter its unrelated technical specifications.
      if (standardTemplate) {
        const details = description.closest('.shopify-section');
        const sourceText = source.items.map(item => cleanText(item.textContent)).join('\n');
        details?.querySelectorAll('.tab-content ul, .tab-content ol').forEach(list => {
          const candidateText = [...list.children].filter(item => item.tagName === 'LI').map(item => cleanText(item.textContent)).join('\n');
          if (candidateText !== sourceText) return;
          const original = cutaways[0].querySelector('img');
          const image = original.cloneNode(true);
          image.className = 'orion-construction-labels__image';
          image.src = original.getAttribute('src') || original.dataset.src;
          image.srcset = original.getAttribute('srcset') || original.dataset.srcset || '';
          image.sizes = '(min-width: 990px) 400px, 45vw';
          image.loading = 'lazy';
          const layout = this.legacyLayout(image, model, gallery, cutaways[0]);
          list.before(layout);
          this._sourceState.push({ element: list, hidden: list.hidden });
          this._legacy.push({ layout });
        });
        details?.querySelectorAll('.tab-content img.tab-img').forEach(image => {
          let originalAttributes;
          if (recoveredCutaway) {
            originalAttributes = ['src', 'srcset', 'sizes', 'width', 'height'].map(name => [name, image.getAttribute(name)]);
            image.src = reviewedImage.dataset.src || reviewedImage.getAttribute('src');
            image.srcset = reviewedImage.dataset.srcset || reviewedImage.getAttribute('srcset') || '';
            image.sizes = '(min-width: 990px) 400px, 45vw';
            for (const name of ['width', 'height']) image.setAttribute(name, reviewedImage.getAttribute(name));
          }
          const marker = document.createComment('construction-image');
          image.before(marker);
          const layout = this.legacyLayout(image, model, gallery, cutaways[0]);
          marker.replaceWith(layout);
          this._legacy.push({ layout, originalImage: image, originalAttributes });
        });
      }

      // Hide only after the complete ordered list has been copied into each
      // construction display. Global Shopify product descriptions are untouched.
      this._sourceState.push(...[source.heading, source.list].map(element => ({ element, hidden: element.hidden })));
      this._sourceState.forEach(({ element }) => { element.hidden = true; element.classList.add('orion-construction-source'); });
      gallery.setAttribute('data-construction-labels', '');
      // In a short landscape viewport the complete list can be scrolled. Its
      // native scrolling must not be captured by the viewer's image-pan surface.
      const dialog = gallery.querySelector('orion-image-viewer')?.dialog;
      if (dialog) {
        const allowListScroll = event => {
          const list = event.target.closest?.('.orion-construction-labels__list');
          if (!list || list.scrollHeight <= list.clientHeight + 1) return;
          if (event.type === 'keydown' && !['ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown', ' '].includes(event.key)) return;
          event.stopPropagation();
        };
        for (const type of ['wheel', 'pointerdown', 'pointermove', 'pointerup', 'keydown']) {
          dialog.addEventListener(type, allowListScroll, { capture: true, passive: true, signal: this._abort.signal });
        }
      }
      this._enhanced = true;
      this.dataset.ready = 'true';
      const measure = () => {
        if (!this.isConnected) return;
        const needed = Math.ceil(Math.max(...this._decorated.map(({ list }) => list.scrollHeight)) + 100);
        const value = needed + 'px';
        if (gallery.style.getPropertyValue('--orion-construction-min-height') !== value) gallery.style.setProperty('--orion-construction-min-height', value);
      };
      this._resize = new ResizeObserver(measure);
      this._decorated.forEach(({ list }) => this._resize.observe(list));
      measure();
    }

    attachViewer(button, gallery, cutaway) {
      button.addEventListener('click', async event => {
        if (!gallery._shown || !gallery.goTo || !gallery.openZoom) return;
        const index = Number(cutaway.dataset.view);
        if (!gallery.usableIndices().includes(index)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        // No stopDemo(): the viewer pauses autoplay while it is open and it resumes on close.
        await gallery.goTo(index, undefined, true);
        gallery.openZoom();
      }, { capture: true, signal: this._abort.signal });
    }

    legacyLayout(image, model, gallery, cutaway) {
      const layout = document.createElement('div');
      layout.className = 'orion-construction-layout orion-construction-layout--legacy';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'orion-construction-labels__open';
      button.setAttribute('aria-label', gallery.querySelector('[data-gallery-zoom]').getAttribute('aria-label'));
      button.appendChild(image);
      layout.append(button, this.makeList(model));
      this.attachOriginViewer(button, gallery, cutaway);
      return layout;
    }

    // Zoom from this image with the gallery's own viewer (same motion as the gallery above) without moving the gallery.
    // Falls back to the section's lightbox when the viewer is not available.
    attachOriginViewer(button, gallery, cutaway) {
      button.addEventListener('click', event => {
        const viewer = gallery.querySelector('orion-image-viewer');
        const index = Number(cutaway.dataset.view);
        const image = button.querySelector('img');
        if (typeof viewer?.openView !== 'function' || !gallery._shown || !image || !gallery.usableIndices?.().includes(index)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        viewer.openView({ index, image, clip: button, returnFocus: button });
      }, { capture: true, signal: this._abort.signal });
    }

    restore() {
      this._resize?.disconnect();
      if (this._anatomyImageSource) {
        const { image, attributes } = this._anatomyImageSource;
        attributes.forEach(([name, value]) => value === null ? image.removeAttribute(name) : image.setAttribute(name, value));
        this._anatomyImageSource = null;
      }
      this._sourceState?.forEach(({ element, hidden }) => { element.hidden = hidden; element.classList.remove('orion-construction-source'); });
      this._decorated?.forEach(({ view, plane, list }) => {
        list.remove();
        plane.classList.remove('orion-construction-layout', 'orion-construction-layout--tall');
        plane.style.removeProperty('--orion-layer-count');
        plane.style.removeProperty('--orion-stack-ratio');
        view.classList.remove('orion-construction-view');
      });
      if (this._anatomy) {
        if (this._anatomy.caption) this._anatomy.button.append(...this._anatomy.caption.childNodes);
        this._anatomy.layout.before(this._anatomy.button);
        this._anatomy.layout.remove();
      }
      this._legacy?.forEach(({ layout, originalImage, originalAttributes }) => {
        originalAttributes?.forEach(([name, value]) => value === null ? originalImage.removeAttribute(name) : originalImage.setAttribute(name, value));
        if (originalImage) layout.before(originalImage);
        layout.remove();
      });
      this._gallery?.removeAttribute('data-construction-labels');
      this._gallery?.style.removeProperty('--orion-construction-min-height');
      this._enhanced = false;
      this._anatomy = null;
      this._sourceState = [];
      this._decorated = [];
      this._legacy = [];
      delete this.dataset.ready;
    }

    disconnectedCallback() {
      cancelAnimationFrame(this._frame);
      this._abort?.abort();
      this.restore();
      this._connected = false;
    }
  }
  customElements.define('orion-construction-labels', OrionConstructionLabels);
})();

